import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { navigate } from 'gatsby-link'
import TopSide from '../Head/TopSide'
import accountForm from '../../content/account-form'
import { citizenshipsKz } from '../../content/countries'
import RegionCityList from '../../content/region-city'
import RegionCityListEn from '../../content/region-city_en'
import RegionCityListKz from '../../content/region-city_kz'
import KzRegionCityList from '../../content/kz_region-city'
import KzRegionCityListEn from '../../content/kz_region-city_eng'
import KzRegionCityListKz from '../../content/kz_region-city_kz'
import AcceptConditions from '../AcceptConditions/AcceptConditions'
import ScreenOneRus from '../../Layouts/Physical/RusInstance/ScreenOne'
import ScreenOneKzt from '../../Layouts/Physical/KztInstance/ScreenOne'
import ScreenTwo from '../../Layouts/Physical/ScreenTwo'
import ScreenThree from '../../Layouts/Physical/ScreenThree'
import ScreenThreeCart from '../../Layouts/Physical/ScreenThreeCart'
import ScreenFour from '../../Layouts/Physical/ScreenFour'
import FiscalScreenOne from '../../Layouts/Fiscal/FiscalScreenOne'
import FiscalScreenTwo from '../../Layouts/Fiscal/FiscalScreenTwo'
import FiscalScreenThree from '../../Layouts/Fiscal/FiscalScreenThree'
import FiscalScreenFour from '../../Layouts/Fiscal/FiscalScreenFour'
import ConfirmRemember from '../../Layouts/ConfirmRemember/ConfirmRemember'
import { iframeResizer } from 'iframe-resizer'
import { getInitialState, INITIAL_STATE, profileType_Fiscal } from '../../state-actions-reducers'
import { saveState, resetLocalStorage, saveCart, getSavedOrderId } from '../../local-storage'
import { useFormStore } from '../../store/formStore'
import { useDefer } from '../../utils/helpers'
import { useToken, getUserAndFormId } from '../../hooks/useTokenCustomHook'
import { useFormSettings } from '../../hooks/useFormSettings'
import { useFormValidation } from '../../hooks/useFormValidation'
import { useCountryLists } from '../../hooks/useCountryLists'
import { COUNTRY } from '../../server-parameters'
import styles from './IndexPresentationComponent.module.scss'
import BackButtonImg from '../../images/black-arrow-left.svg'
import { useQueryContext } from '../../pages'
import '../../styles/customstyles.css'
import { Lang } from '../../types/common-types'
import LoadingScreen from '../../Layouts/LoadingScreen/LoadingScreen'
import InfoMessage from '../../shared/InfoMessage/InfoMessage'
import Footer from '../Footer/Footer'
import cn from 'classnames'

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Return the correct region/city list for the given lang and country code. */
function getRegionCityList(lang: string | null, countryCode: string) {
  if (lang === 'kz') {
    if (countryCode === '643') return RegionCityListKz
    if (countryCode === '398') return KzRegionCityListKz
  } else if (lang === 'eng') {
    if (countryCode === '643') return RegionCityListEn
    if (countryCode === '398') return KzRegionCityListEn
  } else {
    if (countryCode === '643') return RegionCityList
    if (countryCode === '398') return KzRegionCityList
  }
  return []
}

// ────────────────────────────────────────────────────────────────────────────

export default function Index() {
  const query = useQueryContext()
  const [searchParams, setSearchParams] = useState(new URLSearchParams())
  useEffect(() => { setSearchParams(new URLSearchParams(query)) }, [query])

  const [lang, setLang] = useState<Lang>('rus')
  const [parentUrl, setParentUrl] = useState('')
  const [cartList, setCartList] = useState('[]')
  const [activeForm, setActiveForm] = useState<'Physical' | 'Company' | 'Individual'>('Physical')
  const [initialState, setInitialState] = useState(INITIAL_STATE)

  // ── RHF ──────────────────────────────────────────────────────────────────
  const methods = useForm({ defaultValues: INITIAL_STATE })

  // Stable counter — increments only on real value changes (not on every render).
  // Using methods.watch() directly in useDefer deps causes infinite loops
  // because watch() returns a new object reference on every render.
  const [formChangeCount, setFormChangeCount] = useState(0)
  useEffect(() => {
    const { unsubscribe } = methods.watch(() => setFormChangeCount(c => c + 1))
    return unsubscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formValues = methods.watch()

  // ── Navigation (Zustand) ─────────────────────────────────────────────────
  const {
    currentStep,
    setCurrentStep,
    incrementStep,
    decrementStep,
    setStrictValidation,
    reset: resetNav,
  } = useFormStore()

  const state = { ...formValues, currentStep }
  const step = currentStep
  const TOTAL_STEPS = formValues.profileType === profileType_Fiscal ? 5 : 4

  // ── Settings + citizenships ───────────────────────────────────────────────
  const { loading, instanceSettings, settingsCitizenship } = useFormSettings()

  // Restore persisted form state on mount
  useEffect(() => {
    const loadedState = getInitialState(window.localStorage)
    setInitialState(loadedState)
    methods.reset(loadedState)

    const params = new URLSearchParams(location.search)
    let _step = params.get('step') ? +('0' + params.get('step')) : 0
    if (!_step || !loadedState.agreed || _step > TOTAL_STEPS) _step = 0
    setCurrentStep(_step)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch userId + formId
  useToken(({ userId, formId }) => {
    methods.setValue('userId', userId)
    methods.setValue('formId', formId)
  }, [])

  // Parse lang / parentUrl from URL params
  useEffect(() => {
    const langParam = searchParams.get('lang')
    if (langParam === 'eng') { setLang('eng'); methods.setValue('locale', 'EN') }
    else if (langParam === 'kz') { setLang('kz');  methods.setValue('locale', 'KZ') }
    setParentUrl(searchParams.get('parentUrl') ?? '')
  }, [searchParams])

  // Parse cart items from URL params
  useEffect(() => {
    const ids = searchParams.getAll('id')
    if (!ids.length) return
    const cart = ids.map((_, key) => ({
      guid:         searchParams.getAll('guid')[key],
      id:           searchParams.getAll('id')[key],
      img:          searchParams.getAll('img')[key],
      name:         searchParams.getAll('name')[key],
      count:        searchParams.getAll('count')[key],
      startDateIso: new Date(searchParams.getAll('startDateIso')[key]).toISOString(),
      startDate:    searchParams.getAll('startDate')[key],
      startTime:    searchParams.getAll('startTime')[key],
      endDateIso:   new Date(searchParams.getAll('endDateIso')[key]).toISOString(),
      endDate:      searchParams.getAll('endDate')[key],
      endTime:      searchParams.getAll('endTime')[key],
      valuta:       searchParams.getAll('valuta')[key],
      valutaRate:   searchParams.getAll('valutaRate')[key],
      price:        searchParams.getAll('price')[key],
      discount:     searchParams.getAll('discount')[key],
      shift:        searchParams.getAll('shift')[key],
    }))
    setCartList(JSON.stringify(cart))
    const products = cart.map(p => ({ title: p.name, guid: p.guid }))
    if (products.length > 0) methods.setValue('goods', products as any)
  }, [searchParams])

  // Persist form to localStorage (debounced)
  useDefer(
    () => saveState({ ...methods.getValues(), currentStep } as any, localStorage),
    [formChangeCount, currentStep]
  )

  // Notify the parent site once the client actually starts filling the form
  // (field edit / checkbox-radio-select / step change) — not just on the
  // initial restore-from-localStorage reset, which also bumps formChangeCount
  // but leaves formState.isDirty false since it resets the dirty baseline.
  const hasNotifiedStartedRef = useRef(false)
  useEffect(() => {
    if (!hasNotifiedStartedRef.current && methods.formState.isDirty && window.parentIFrame) {
      window.parentIFrame.sendMessage({ anketaStarted: true })
      hasNotifiedStartedRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formChangeCount])

  // Skip step 0 if user already agreed
  useEffect(() => {
    if (currentStep === 0 && state.agreed) setCurrentStep(1)
  }, [currentStep, state.agreed])

  // Sync URL with current step
  useEffect(() => {
    navigate(`?lang=${lang}&country=${COUNTRY}&t=${state.profileType}&step=${step}`)
  }, [step])

  // iFrame communication
  useEffect(() => {
    if (window.parentIFrame) {
      window.parentIFrame.size()
      if (step === 5) {
        window.parentIFrame.sendMessage({
          city:        state.mainCity,
          name:        state.firstName,
          lastName:    state.lastName,
          patronymic:  state.patronymic,
          mail:        state.emails[0],
          phone:       state.mobilePhoneNumbers[0],
          comments:    state.feedback,
          accessToken: activeForm === 'Physical' ? getSavedOrderId(window.localStorage) : undefined,
          anketaStarted: false,
        })
      } else {
        window.parentIFrame.sendMessage('scrollTop')
      }
    }
  }, [step])

  useEffect(() => {
    window.iFrameResizer = {
      onMessage: (event: any) => {
        if (event?.cart) saveCart(JSON.stringify(event.cart), localStorage)
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeResizer])

  // ── Validation ────────────────────────────────────────────────────────────
  const { setNextStep, invalidFields } = useFormValidation({
    methods,
    formChangeCount,
    currentStep,
    imageError: state.imageError,
  })

  // ── State helpers ─────────────────────────────────────────────────────────
  function handleResetState() {
    methods.reset(INITIAL_STATE)
    resetNav()
    resetLocalStorage(window.localStorage)
    getUserAndFormId(() => {}, window.localStorage)
    if (lang) methods.setValue('locale', lang.toUpperCase())
    hasNotifiedStartedRef.current = false
    if (window.parentIFrame) window.parentIFrame.sendMessage({ anketaStarted: false })
  }

  function prevStepWithNewToken() {
    getUserAndFormId(({ userId, formId }) => {
      methods.setValue('userId', userId)
      methods.setValue('formId', formId)
      setStrictValidation(false)
      methods.clearErrors()
      decrementStep()
    }, localStorage)
  }

  function goBack() {
    if (step === 1) {
      handleResetState()
    } else {
      setStrictValidation(false)
      methods.clearErrors()
      decrementStep()
    }
  }

  function scrollToError() {
    const invalidEl = document.querySelector('[class*="invalidText"]')
    if (!invalidEl) return null
    const input = invalidEl.closest('form, div, section, li, td, tr')?.querySelector('input')
      ?? document.querySelector('input')
    if (input) { (input as HTMLElement).focus() }
    return input
  }

  // ── Country lists ─────────────────────────────────────────────────────────
  const {
    citizenshipsList,
    allFiscalSort,
    allCitizenshipsFiscalSort,
    allCitizenshipsAddressSort,
    allCitizenshipsFiscalAddressSort,
  } = useCountryLists({ settingsCitizenship, lang })

  const langParam = searchParams.get('lang')
  const RegionCityListLocalesRegistered = useMemo(() => getRegionCityList(langParam, state.addressRegisteredCountry), [langParam, state.addressRegisteredCountry])
  const RegionCityListLocalesFactual    = useMemo(() => getRegionCityList(langParam, state.addressFactualCountry),    [langParam, state.addressFactualCountry])
  const RegionCityListLocalesResidence  = useMemo(() => getRegionCityList(langParam, state.addressResidenceCountry),  [langParam, state.addressResidenceCountry])

  // ── Shared screen props ───────────────────────────────────────────────────
  const topSideProps = { profileType: state.profileType, accountForm, lang, step, totalsteps: TOTAL_STEPS }
  const screenFourProps = {
    lang, accountForm,
    resetState: handleResetState,
    nextStep: incrementStep,
    prevStep: prevStepWithNewToken,
  }
  const confirmProps = {
    lang, accountForm,
    onAccept: handleResetState,
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return <LoadingScreen />

  return (
    <FormProvider {...methods}>
      <div className="container">
        {/* Agreement / step 0 */}
        {(!state.agreed && step !== TOTAL_STEPS) || step === 0 ? (
          <AcceptConditions
            lang={lang}
            parentUrl={parentUrl}
            accountForm={accountForm}
            availableClient={instanceSettings?.availableClient}
            activeForm={activeForm}
            changeActiveForm={setActiveForm}
          />
        ) : state.profileType === profileType_Fiscal ? (
          /* ── Fiscal flow ── */
          <>
            {step < 5 && (
              <div className="row">
                <div className={styles.backButton + ' col-12'} onClick={goBack}>
                  <img src={BackButtonImg} alt="back" />
                  <b>{accountForm.prevButton[lang]}</b>
                </div>
              </div>
            )}
            <form className={styles.form} onSubmit={e => e.preventDefault()}>
              {step === 1 && <main><TopSide {...topSideProps} /><FiscalScreenOne lang={lang} accountForm={accountForm} citizenshipCountriesList={allCitizenshipsFiscalSort} countriesList={allCitizenshipsFiscalSort} allFiscalCountry={allFiscalSort} addressCountries={allCitizenshipsFiscalAddressSort} regionCityRegistered={RegionCityListLocalesRegistered} regionCityFactual={RegionCityListLocalesFactual} regionCityResidence={RegionCityListLocalesResidence} regionCity={RegionCityList} /></main>}
              {step === 2 && <main><TopSide {...topSideProps} /><FiscalScreenTwo lang={lang} accountForm={accountForm} /></main>}
              {step === 3 && <main><TopSide {...topSideProps} /><FiscalScreenThree lang={lang} accountForm={accountForm} allCitizenships={citizenshipsKz} /></main>}
              {step === 4 && <main><TopSide {...topSideProps} showInfoMessage /><FiscalScreenFour lang={lang} accountForm={accountForm} allCitizenships={citizenshipsKz} /></main>}
              {step === 5 && <main><TopSide {...topSideProps} /><ScreenFour {...screenFourProps} /></main>}
              {step === 6 && <main><ConfirmRemember {...confirmProps} /></main>}
            </form>
          </>
        ) : (
          /* ── Physical flow ── */
          <>
            {step < 4 && (
              <div className="row">
                <div className={styles.backButton + ' col-12'} onClick={goBack}>
                  <img src={BackButtonImg} alt="back" />
                  <b>{accountForm.prevButton[lang]}</b>
                </div>
              </div>
            )}
            <form className={styles.form} onSubmit={e => e.preventDefault()}>
              {step === 1 && (
                <main>
                  <TopSide {...topSideProps} />
                  {COUNTRY === 'kz'
                    ? <ScreenOneKzt lang={lang} accountForm={accountForm} citizenshipCountriesList={citizenshipsList} countriesList={citizenshipsList} allCitizenships={citizenshipsList} addressCountries={allCitizenshipsAddressSort} regionCityRegistered={RegionCityListLocalesRegistered} regionCityFactual={RegionCityListLocalesFactual} />
                    : <ScreenOneRus lang={lang} accountForm={accountForm} citizenshipCountriesList={citizenshipsList} countriesList={citizenshipsList} allCitizenships={citizenshipsList} addressCountries={allCitizenshipsAddressSort} regionCityRegistered={RegionCityListLocalesRegistered} regionCityFactual={RegionCityListLocalesFactual} />
                  }
                </main>
              )}
              {step === 2 && <main><TopSide {...topSideProps} /><ScreenTwo lang={lang} accountForm={accountForm} allCitizenships={citizenshipsKz} /></main>}
              {step === 3 && (
                <main>
                  <TopSide {...topSideProps} showInfoMessage />
                  {JSON.parse(cartList).length > 0
                    ? <ScreenThreeCart lang={lang} accountForm={accountForm} cart={JSON.parse(cartList)} />
                    : <ScreenThree     lang={lang} accountForm={accountForm} />
                  }
                </main>
              )}
              {step === 4 && <main><TopSide {...topSideProps} /><ScreenFour {...screenFourProps} cart={JSON.parse(cartList)} /></main>}
              {step === 5 && <main><ConfirmRemember {...confirmProps} /></main>}
            </form>
          </>
        )}

        {/* Footer with validation alert */}
        {instanceSettings?.availableClient && (
          <div className={styles.AnketaFooter}>
            {(invalidFields.length > 0 || !(state as any).isIdentityOk || state.imageError.length > 0 || state.serverError) && (
              <InfoMessage
                className={cn((state as any).isIdentityOk && !state.serverError && styles.pointer)}
                type="alarm"
                text={
                  !(state as any).isIdentityOk
                    ? <span>{accountForm?.validationErrorClientExist?.[lang]}</span>
                    : state.serverError
                      ? <span>{accountForm.serverError[lang]}</span>
                      : <span onClick={scrollToError}>{accountForm.validationError[lang]}</span>
                }
              />
            )}
            <Footer
              lang={lang}
              parentUrl={parentUrl}
              accountForm={accountForm}
              step={step}
              totalSteps={TOTAL_STEPS}
              activeForm={activeForm}
              handleResetState={handleResetState}
              setStrictValidation={setStrictValidation}
              clearErrors={methods.clearErrors}
              setNextStep={setNextStep}
            />
          </div>
        )}
      </div>
    </FormProvider>
  )
}
